import { AlertCircle, ChevronDown, TriangleAlert } from "lucide-react";
import { useMemo, useState } from "react";

import { Button } from "components/ui/button";

import { formatDayHeading } from "../../event-experience/agenda-schedule";
import { DAY_TITLE_MAX, DAY_TITLES_MAX } from "../../events/landing";
import {
  dayTitleProblems,
  dayTitlesChange,
  dayTitlesFormFrom,
  eventDays,
  settleDayTitleEdits,
  staleDayTitles,
} from "../day-titles";
import type { EventSettings } from "../schemas";
import { useEventSettingsWrite } from "../use-event-settings-writes";
import { LimitedField } from "./LimitedField";

/**
 * One short title per day of the event, shown above that day's sessions on the
 * landing page. The days come from the event's start and end dates, so there
 * is a field for exactly the days the event has.
 *
 * Collapsed until opened, because a week-long event is a long form and the
 * agenda is what this tab is for. Saving writes the whole `dayTitles` map,
 * without empty entries, and removes the key when nothing is left. It goes
 * through the same event write as the Settings cards.
 */
export function DayTitlesEditor({
  eventId,
  event,
  reload,
}: {
  eventId: string;
  event: Pick<EventSettings, "startDate" | "endDate" | "dayTitles">;
  reload: () => void;
}) {
  const { save, saving, error } = useEventSettingsWrite(eventId);
  const { days, truncated } = useMemo(
    () => eventDays(event.startDate, event.endDate),
    [event.startDate, event.endDate],
  );
  // What the organiser typed, per day. The rows are derived from the event's
  // current days and stored titles on every render, so a change of dates or a
  // reload cannot leave rows behind or hide a stored title; only unsaved text
  // is state.
  const [edits, setEdits] = useState<Record<string, string>>({});
  const form = dayTitlesFormFrom(days, event.dayTitles, edits);
  const inputsKey = JSON.stringify([days, event.dayTitles ?? {}]);
  const [settledFor, setSettledFor] = useState(inputsKey);
  if (settledFor !== inputsKey) {
    setSettledFor(inputsKey);
    setEdits(settleDayTitleEdits(edits, days, event.dayTitles));
  }
  const [tried, setTried] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const change = dayTitlesChange(form, days, event.dayTitles);
  const problems = dayTitleProblems(form);
  const stale = staleDayTitles(days, event.dayTitles);
  const titled = Object.keys(event.dayTitles ?? {}).length;

  async function handleSave() {
    setTried(true);
    setStatus(null);
    if (!change || Object.keys(problems).length > 0) return;
    if (await save(change)) {
      setStatus("Day titles saved");
      reload();
    }
  }

  return (
    <details
      className="rounded-lg border border-border bg-card p-5 text-card-foreground"
      data-testid="day-titles"
    >
      <summary
        className="flex min-h-11 cursor-pointer items-center justify-between gap-3 text-sm font-semibold text-foreground"
        data-testid="day-titles-summary"
      >
        <span>
          Day titles
          {titled > 0 && (
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              {titled} set
            </span>
          )}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0" aria-hidden="true" />
      </summary>

      <div className="space-y-4 pt-3">
        <p className="text-sm text-muted-foreground">
          A short title for each day, shown above that day&apos;s sessions on
          the landing page. Leave a day empty to show no title.
        </p>

        {days.length === 0 ? (
          <p
            className="text-sm text-muted-foreground"
            data-testid="day-titles-no-days"
          >
            This event has no valid start and end dates. Set them on the
            Settings tab to add day titles.
          </p>
        ) : (
          <>
            <div className="space-y-4" data-testid="day-titles-list">
              {days.map((day) => (
                <LimitedField
                  key={day}
                  id={`day-title-${day}`}
                  label={formatDayHeading(day, "en-GB")}
                  value={form[day] ?? ""}
                  max={DAY_TITLE_MAX}
                  count={(form[day] ?? "").trim().length}
                  disabled={saving}
                  error={tried ? problems[day] : undefined}
                  onChange={(title) => {
                    setStatus(null);
                    setEdits({ ...edits, [day]: title });
                  }}
                />
              ))}
            </div>

            {truncated && (
              <p
                className="text-sm text-muted-foreground"
                data-testid="day-titles-truncated"
              >
                Only the first {DAY_TITLES_MAX} days can have a title.
              </p>
            )}
            {stale.length > 0 && (
              <p
                className="flex items-start gap-2 rounded-md border border-status-warning bg-status-warning/15 px-3 py-2 text-sm text-foreground"
                data-testid="day-titles-stale"
              >
                <TriangleAlert
                  className="mt-0.5 h-4 w-4 shrink-0"
                  aria-hidden="true"
                />
                {stale.length === 1
                  ? "1 title for a day outside the event's dates is"
                  : `${stale.length} titles for days outside the event's dates are`}{" "}
                removed when you save.
              </p>
            )}

            {error && (
              <p
                role="alert"
                className="flex items-start gap-2 text-sm text-destructive-text"
                data-testid="day-titles-error"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                {error}
              </p>
            )}
          </>
        )}

        {/* Always rendered, and visually hidden rather than display:none while
            empty, so a screen reader has the live region before a message
            arrives; one that appears with its text is often not read. */}
        <p
          role="status"
          className={status ? "text-sm text-foreground" : "sr-only"}
          data-testid="day-titles-status"
        >
          {status}
        </p>

        {days.length > 0 && (
          <Button
            variant="secondary"
            onClick={() => void handleSave()}
            disabled={saving || !change}
            className="max-sm:w-full"
            data-testid="save-day-titles"
          >
            {saving ? "Saving…" : "Save day titles"}
          </Button>
        )}
      </div>
    </details>
  );
}
